import { useRef, useState, useEffect, useCallback } from "react";
import { RefreshCw } from "lucide-react";

/**
 * iOS / Android-style pull-to-refresh wrapper.
 *
 * Wraps `children` with a touch listener that detects a downward pull when the
 * page is scrolled to the top, then triggers `onRefresh()` once the user
 * releases past the threshold.
 *
 * Renders a small floating spinner badge that scales + rotates with the pull,
 * then spins while `onRefresh()` resolves.
 *
 * Usage:
 *   <PullToRefresh onRefresh={loadStuff}>
 *     <YourPageContent />
 *   </PullToRefresh>
 */
export function PullToRefresh({ onRefresh, children, threshold = 70, maxPull = 130 }) {
  const containerRef = useRef(null);
  const startY = useRef(0);
  const activeRef = useRef(false);
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  const handleRefresh = useCallback(async () => {
    if (!onRefresh) return;
    setRefreshing(true);
    try {
      await onRefresh();
    } finally {
      // Small linger so the spinner animation reads as intentional.
      setTimeout(() => {
        setRefreshing(false);
        setPull(0);
      }, 400);
    }
  }, [onRefresh]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const isAtTop = () =>
      (window.scrollY || window.pageYOffset || document.documentElement.scrollTop || 0) <= 1;

    const onTouchStart = (e) => {
      if (!isAtTop() || refreshing) return;
      startY.current = e.touches[0].clientY;
      activeRef.current = true;
    };

    const onTouchMove = (e) => {
      if (!activeRef.current || refreshing) return;
      const dy = e.touches[0].clientY - startY.current;
      if (dy <= 0) {
        // user swiped back up — cancel the pull gesture
        if (pull > 0) setPull(0);
        activeRef.current = false;
        return;
      }
      // Apply rubber-band resistance.
      const resisted = Math.min(maxPull, dy * 0.55);
      setPull(resisted);
      // Once we're tracking the pull, prevent the browser's own overscroll
      // bounce so the indicator stays glued to the finger.
      if (resisted > 6 && e.cancelable) {
        e.preventDefault();
      }
    };

    const onTouchEnd = () => {
      if (!activeRef.current) return;
      activeRef.current = false;
      if (pull >= threshold && !refreshing) {
        handleRefresh();
      } else {
        setPull(0);
      }
    };

    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchmove", onTouchMove, { passive: false });
    el.addEventListener("touchend", onTouchEnd, { passive: true });
    el.addEventListener("touchcancel", onTouchEnd, { passive: true });

    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("touchend", onTouchEnd);
      el.removeEventListener("touchcancel", onTouchEnd);
    };
  }, [pull, refreshing, threshold, maxPull, handleRefresh]);

  const progress = Math.min(1, pull / threshold);
  // Visible offset for the floating indicator + content shift.
  const indicatorY = refreshing ? 60 : pull * 0.7;
  const contentShift = refreshing ? 24 : pull * 0.35;

  return (
    <div ref={containerRef} className="relative">
      <div
        aria-hidden={pull === 0 && !refreshing}
        className="absolute left-0 right-0 top-0 z-40 flex justify-center pointer-events-none"
        style={{
          transform: `translate3d(0, ${indicatorY - 50}px, 0)`,
          transition: pull && !refreshing ? "none" : "transform 280ms cubic-bezier(0.22, 1, 0.36, 1)",
        }}
        data-testid="pull-to-refresh-indicator"
      >
        <div
          className="w-11 h-11 rounded-full border border-white/10 bg-black/85 backdrop-blur-md flex items-center justify-center shadow-[0_4px_16px_rgba(0,0,0,0.5)]"
          style={{
            transform: `scale(${0.55 + progress * 0.55})`,
            opacity: refreshing || pull > 0 ? 1 : 0,
            transition: "opacity 180ms ease-out, transform 80ms ease-out",
          }}
        >
          <RefreshCw
            className={`w-5 h-5 text-[#FF3B30] ${refreshing ? "animate-spin" : ""}`}
            style={refreshing ? undefined : { transform: `rotate(${pull * 4}deg)` }}
          />
        </div>
      </div>
      <div
        style={{
          transform: `translate3d(0, ${contentShift}px, 0)`,
          transition: pull && !refreshing ? "none" : "transform 320ms cubic-bezier(0.22, 1, 0.36, 1)",
          willChange: "transform",
        }}
      >
        {children}
      </div>
    </div>
  );
}
